import {BaseMuixApi} from "~/components/muix-data-grid/BaseMuixApi";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {complianceRequirementGridUrl} from "~/pages/supplier-360/services/supplier360Urls";
import type {
    ComplianceBuAggregate,
    ComplianceRequirementGridRow,
} from "~/pages/supplier-360/types/Supplier360Types";

/** The MUIX params for the records grid grouped by BU (§2.8a), unpaginated (`no_page`). */
function groupedParams(entityId: number, databagId: number, groupKeys: string[]): URLSearchParams {
    const params = BaseMuixApi.getMuiXQuery(databagId, {groupFields: ['bu'], groupKeys, noPage: true});
    params.set('entity', String(entityId));
    return params;
}

/**
 * Per-BU requirement counts — `compliance_requirement/muix/grid/` with `groupFields=["bu"]` (§2.8a).
 * One row per BU (`total_agg` / `met_agg` / `expiring_agg` / `pending_agg` / `unmet_agg`), plus a
 * `null` group for company-wide records linked to no BU. Shares the requirement prefix, so record
 * and evidence writes refresh it.
 */
export function useComplianceBuAggregatesQuery(entityId: number, databagId: number | undefined) {
    const url = complianceRequirementGridUrl();
    const params = databagId === undefined ? undefined : groupedParams(entityId, databagId, []);
    return useCustomQuery({
        queryKey: [url, {params: params?.toString()}] as const,
        queryFn: async ({signal}) => (await queryHttp.get<ComplianceBuAggregate[]>(url, {params, signal})).data,
        enabled: Boolean(entityId) && databagId !== undefined,
    });
}

/**
 * One BU's requirements — the same grouped grid expanded on that BU (`groupKeys=[id]`, §2.8a): its
 * own business-unit records plus the company-wide ones linked to it through `applies_to`.
 */
export function useComplianceBuRequirementsQuery(entityId: number, databagId: number | undefined, buQualificationId: number) {
    const url = complianceRequirementGridUrl();
    const params = databagId === undefined ? undefined : groupedParams(entityId, databagId, [String(buQualificationId)]);
    return useCustomQuery({
        queryKey: [url, {params: params?.toString()}] as const,
        queryFn: async ({signal}) => (await queryHttp.get<ComplianceRequirementGridRow[]>(url, {params, signal})).data,
        enabled: Boolean(entityId) && databagId !== undefined,
    });
}
